import ReactApexChart from "react-apexcharts";
import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { productAnalyticsQueries } from "@/api/product-analytics";
import { DateRange } from "@/lib/date";
import { ApexOptions } from "apexcharts";
import { safeParseDate } from "@/lib/utils";

const CHART_OPTIONS = {
  chart: {
    type: "bar",
    stacked: false,
    zoom: { enabled: false },
    toolbar: { show: false },
    //https://github.com/apexcharts/react-apexcharts/issues/187#issuecomment-901969532
    events: {
      mounted: (chart) => {
        chart.windowResizeHandler();
      },
    },
  },
  plotOptions: {
    bar: {
      horizontal: false,
      columnWidth: "45%",
    },
  },
  xaxis: {
    type: "datetime",
    labels: {
      format: "MMM dd",
      style: {
        colors: "#ffffff", // white y-axis labels
      },
    },
  },
  yaxis: {
    labels: {
      formatter: (val: number) => val.toLocaleString(),
      style: {
        colors: "#ffffff", // white y-axis labels
      },
    },
    title: { text: "Credits Used" },
  },
  tooltip: {
    theme: "dark", // optional: gives dark styling

    x: { format: "MMM dd, yyyy" },
    y: { formatter: (val: number) => val.toLocaleString() },
  },
  legend: {
    show: true,
    position: "top",
    labels: {
      colors: "#ffffff",
    },
  },
  dataLabels: { enabled: false },
} satisfies ApexOptions;

export function ProductsUsageChart({ dateRange }: { dateRange: DateRange }) {
  const { data: allProductsSummary } = useQuery(
    productAnalyticsQueries.summary(
      dateRange.from?.toISOString(),
      dateRange.to?.toISOString(),
    ),
  );

  const allProdBarSeries = useMemo(() => {
    if (!allProductsSummary?.products) return [];
    return Object.entries(allProductsSummary.products).map(([sku, usage]) => {
      const daily = usage.daily_usage ?? [];
      return {
        name: sku,
        data: daily.map((day) => {
          const d = safeParseDate(day.date);
          return {
            x: d ? d.getTime() : day.date,
            y: day.credits ?? 0,
          };
        }),
      };
    });
  }, [allProductsSummary]);

  return (
    <ReactApexChart
      type="bar"
      series={allProdBarSeries}
      options={CHART_OPTIONS}
      height={400}
    />
  );
}
